/* component-product-carousel.js
 * Featured-collection products-carousel enhancement layer.
 * Self-loaded via <script defer> from blocks/_featured-collection-products.liquid
 * when section.settings.layout_type == 'carousel'.
 *
 * Architecture: CSS scroll-snap is the foundational primitive (works without JS for
 * touch/scroll/keyboard arrow-key scrolling). This file enhances with prev/next
 * button navigation, dot-indicator click-to-slide, and active-slide tracking via
 * IntersectionObserver.
 *
 * Per-carousel state lives in the initCarousel(el) closure — multiple carousels on
 * one page operate independently. Compound carousel ID
 * (section.id + block.id) prevents collision when two featured-collection sections
 * appear on the same page.
 *
 * IIFE-dedup-guarded per Rule J12 — safe to inject multiple times via separate
 * <script> tags (browser dedupes by URL anyway, but the guard is belt-and-braces).
 *
 * Reduced-motion: detected once at init via matchMedia; programmatic scroll calls
 * use 'auto' instead of 'smooth' when set. Mirrors the CSS-side
 * @media (prefers-reduced-motion) override of scroll-behavior.
 *
 * Shopify customizer hot-reload: shopify:section:load event re-inits any carousel
 * inside the reloaded section. Old DOM is replaced by Shopify; old listeners + IO
 * GC with their closure.
 */
(function () {
  'use strict';

  if (window.__BLOCKDEDUP_PRODUCT_CAROUSEL__) {
    return;
  }
  window.__BLOCKDEDUP_PRODUCT_CAROUSEL__ = true;

  function initCarousel(carouselEl) {
    var trackEl = carouselEl.querySelector('[data-carousel-track]');
    var prevBtn = carouselEl.querySelector('[data-carousel-prev]');
    var nextBtn = carouselEl.querySelector('[data-carousel-next]');
    var dotsNodeList = carouselEl.querySelectorAll('[data-carousel-dot]');
    var slidesNodeList = carouselEl.querySelectorAll('[data-carousel-slide-index]');

    if (!trackEl || !prevBtn || !nextBtn || slidesNodeList.length === 0) {
      return;
    }

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    var trackInner = trackEl.querySelector('.featured-collection-products__track');

    function getSlideStep() {
      var firstSlide = slidesNodeList[0];
      if (!firstSlide || !trackInner) return 0;
      var slideWidth = firstSlide.offsetWidth;
      var trackStyle = window.getComputedStyle(trackInner);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      return slideWidth + gap;
    }

    function scrollByOneSlide(direction) {
      var step = getSlideStep();
      if (step === 0) return;
      trackEl.scrollBy({
        left: direction * step,
        behavior: scrollBehavior
      });
    }

    function scrollToSlide(idx) {
      var targetSlide = slidesNodeList[idx];
      if (!targetSlide) return;
      var trackBox = trackEl.getBoundingClientRect();
      var slideBox = targetSlide.getBoundingClientRect();
      var offset = slideBox.left - trackBox.left + trackEl.scrollLeft;
      trackEl.scrollTo({ left: offset, behavior: scrollBehavior });
    }

    function setActiveDot(idx) {
      dotsNodeList.forEach(function (dot, i) {
        var isActive = i === idx;
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
        dot.classList.toggle('featured-collection-products__dot--active', isActive);
      });
    }

    function updateEdgeStates() {
      var atStart = trackEl.scrollLeft <= 1;
      var atEnd = trackEl.scrollLeft + trackEl.clientWidth >= trackEl.scrollWidth - 1;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }

    function handleIntersect(entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var idx = parseInt(entry.target.getAttribute('data-carousel-slide-index'), 10);
          if (!isNaN(idx)) setActiveDot(idx);
        }
      });
    }

    prevBtn.addEventListener('click', function () { scrollByOneSlide(-1); });
    nextBtn.addEventListener('click', function () { scrollByOneSlide(1); });

    dotsNodeList.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.getAttribute('data-carousel-dot-index'), 10);
        if (!isNaN(idx)) scrollToSlide(idx);
      });
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(handleIntersect, {
        root: trackEl,
        threshold: 0.6
      });
      slidesNodeList.forEach(function (slide) { io.observe(slide); });
    }

    trackEl.addEventListener('scroll', updateEdgeStates, { passive: true });
    updateEdgeStates();
  }

  function initAll(scopeEl) {
    var root = scopeEl || document;
    var carousels = root.querySelectorAll('.featured-collection-products__carousel[data-carousel-id]');
    carousels.forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });
})();
